import appDisplay from 'scoreboard/components/app-display'
import UnsavedWarning from './unsaved-warning'
import _ from 'lodash'
import guardConstants from './guard-constants'

var debug = false

var guard = {

	/**
	 * Private member variable that keeps track of all of the current checks
	 * that need to be performed to test whether the current screen has
	 * unsaved changes.
	 */
	_guards: [],

	/**
	 * Guards against a user losing unsaved changes. This method may be
	 * called several times if there are multiple forms on the screen (e.g.
	 * SB2.x scorecard builder).
	 *
	 * @param detectDirtyFunction
	 *            a function used to detect if the user entered some data on
	 *            part of the screen that needs to be saved
	 * @param keepChangesFunction
	 *            the function that should be invoked if the user clicks
	 *            that Yes, they would like to save their changes. If
	 *            there's just one form on the screen, go ahead and use this
	 *            function to save the form and be sure to return the
	 *            deferred so the unsaved changes guard will know when the
	 *            save is complete. If clicking Yes should cause the Warning
	 *            to hide and NOT proceed with the originally requested
	 *            action, return HIDE_WARNING or a Deferred that resolves to
	 *            HIDE_WARNING. This will be the case if the
	 *            save failed validation or there are lots of forms on the
	 *            screen so you need to allow the user to interact with each
	 *            of them individually rather than attempting to save them
	 *            all at once.
	 */
	guard: function(detectDirtyFunction, keepChangesFunction) {
		var guard = {
			detectDirtyFunction: detectDirtyFunction.bind(detectDirtyFunction),
			keepChangesFunction: _.isFunction(keepChangesFunction) ?
				keepChangesFunction.bind(keepChangesFunction) : keepChangesFunction
		}
		this._guards.push(guard)
		return guard
	},

	/**
	 * If additional dialogs are needed after a save guard (e.g. admin-groups uses
	 * the dependencies-prevent-action-view to warn users about dataset permissions)
	 * this function allows error handlers the ability to continue with the requested
	 * action (e.g. app-router#goto) after the action is approved.
	 *
	 * @returns {Function} The continue function that was provided to the guard
	 * that is currently open.  If there is no active guard this function will
	 * return undefined.
	 */
	getCurrentContinueFn: function() {
		if (this._warning) {
			return this._warning.options.continueFunction
		}
	},

	/**
	 * Stops guarding against unsaved changes.
	 */
	stopGuarding: function() {
		if (debug) {
			console.log('stopped guarding')
		}
		this._guards = []
	},

	/**
	 * Remove the given guard from the internal list of guards to apply
	 * before allowing navigation to other screens.
	 *
	 * @param guard
	 *            the guard to remove
	 */
	removeGuard: function(guard) {
		var index = _.indexOf(this._guards, guard)
		if (index > -1) {
			// remove the check
			this._guards.splice(index, 1)
		}
	},

	/**
	 * Delegates to #conditionallyRun but accepts options instead of fixed arguments
	 * @param {Function} [options.continueFunction] will be invoked if users chooses 'yes' or 'no'
	 * @param {Function} [options.yesFunction] will be invoked if user chooses 'yes', before the continueFunction
	 * @param {Function} [options.noFunction] will be invoked if user chooses 'no', before the continueFunction
	 * @param {Function} [options.cancelFunction] will only be invoked if users cancels.
	 * @param {Function} [options.errorFunction] Instead of the guard triggering 'cancel'
	 * provide your own error function, the error is provided as a single arg.
	 */
	conditionally: function(options = {}) {
		const {continueFunction, yesFunction, noFunction, cancelFunction,
			errorFunction} = options
		this.conditionallyRun(continueFunction, yesFunction,
			noFunction, cancelFunction, errorFunction)
	},

	/**
	 * Allow the user to save any unsaved changes they may have, then run
	 * the given function. See the constructor of Warning.js for an
	 * explanation of the 4 functions that can be passed to this method.
	 *
	 * @param continueFunction
	 *            the function to run after the user has chosen what to do
	 *            with their unsaved changes
	 * @return <code>UNSAVED_CHANGES_DETECTED</code> (<code>false</code>)
	 *         if we were not able to run the requested function immediately
	 *         because the user had unsaved changes or<br>
	 *         the return value of the given function, if we were able to
	 *         run it right away
	 */
	conditionallyRun: function(continueFunction, yesFunction, noFunction,
		cancelFunction, errorFunction) {

		if (debug) {
			console.log('conditionally running')
		}

		// we there's already a warning displayed, hitch onto that one
		if (this._warning) {
			this._warning.on('continue', continueFunction.bind(continueFunction))
			return guardConstants.UNSAVED_CHANGES_DETECTED
		}

		var state = this.showWarningIfNeeded(continueFunction, yesFunction, noFunction,
			cancelFunction, errorFunction)

		if (state === guardConstants.UNSAVED_CHANGES_NOT_DETECTED) {
			var returnVal = continueFunction()
			this._running = false
			return returnVal
		}

		return state
	},

	/**
	 * Show the unsaved changes warning if needed. Returns
	 * UNSAVED_CHANGES_DETECTED if unsaved changes were detected, or<br>
	 * UNSAVED_CHANGES_NOT_DETECTED, otherwise. See the constructor of
	 * Warning.js for an explanation of the 4 functions that can be passed
	 * to this method.
	 */
	showWarningIfNeeded: function(continueFunction, yesFunction, noFunction,
		cancelFunction, errorFunction) {
		for (var i=0; this._guards && i<this._guards.length; i++) {
			var guard = this._guards[i]
			// if we detect that the form is dirty
			if (guard.detectDirtyFunction()) {
				this._warning = new UnsavedWarning(_.extend({
					keepChangesFunction: guard.keepChangesFunction,
					continueFunction,
					yesFunction,
					noFunction,
					cancelFunction,
					errorFunction
				}, guard.options))
				appDisplay.dialog(this._warning)
				this._warning.on('cancel', function() {
					delete this._warning
				}.bind(this))
				this._warning.on('done error', function() {
					this.stopGuarding()
					delete this._warning
				}.bind(this))
				return guardConstants.UNSAVED_CHANGES_DETECTED
			}
		}
		return guardConstants.UNSAVED_CHANGES_NOT_DETECTED
	},

	/**
	 * @typedef GuardOptions
	 * @property {UnsavedWarningMessages} messages
	 * @property {Boolean} [hideCancel=false]
	 * @property {Function} [keepChangesFn] invoked when the user decides
	 * "yes" they want to keep their changes.
	 */

	/**
	 * A "standard" guard that inspects the dirty property of a view to
	 * determine whether the view can be saved. If the view is found to
	 * be dirty and the user clicks Yes to save changes, this method will
	 * examine the .button-primary to make sure it is not disabled. If the
	 * save button is disabled, that means the user didn't enter enough
	 * information to save, so we will hide the warning and give the user a
	 * chance to enter missing data. If the save button is enabled, we will
	 * call the save method on the view and continue with the user's
	 * originally requested operation.
	 *
	 * @param view
	 *            the view to work with. As previously explained, the
	 *            three key properties this view must have is a dirty
	 *            flag, a .button-primary that is disabled if saves shouldn't
	 *            be allowed and a submit method used to perform saves.  A
	 *            FormView meets all of these criteria.
	 *
	 * @param {GuardOptions} guardOptions
	 * @param {String} [guardOptions.primaryButtonSelector] a selector to use
	 * instead of .button-primary
	 */
	standardGuard: function(view, guardOptions = {}) {

		const defaultKeepChangesFn = () => {
			var okToSave = true
			var numDirty = 0
			for (var i=0; i < this._guards.length; i++) {
				var currentGuard = this._guards[i]
				if (!currentGuard.standardGuardView) {
					okToSave = false
					break
				} else {
					if (currentGuard.detectDirtyFunction()) {
						const primaryButtonSelector = guardOptions.primaryButtonSelector ??
							'.button-primary'
						var primaryButton = currentGuard.standardGuardView.$(primaryButtonSelector)
						if (primaryButton.prop('disabled') || primaryButton.hasClass('is-disabled')) {
							okToSave = false
							break
						} else {
							if (numDirty > 0) {
								okToSave = false
								break
							}
						}
						numDirty++
					}
				}
			}

			if (okToSave) {
				return view.submit()
			} else {
				// #15670 if there are errors, display them
				_.invoke(view, 'validate')
				return guardConstants.HIDE_WARNING
			}
		}

		const guard = this.guard(
			() =>  _.result(view, 'dirty'),
			guardOptions.keepChangesFn || defaultKeepChangesFn)
		guard.standardGuardView = view
		guard.options = guardOptions
		return guard
	}

}

export default guard

guard.__filename__ = 'components/unsaved/guard.js'
